import * as THREE from 'three';
import { GAME_CONFIG } from 'shared';
import type { InputState } from '@core/InputManager';
import type { ThirdPersonCamera } from '@core/ThirdPersonCamera';

export interface PlayerConfig {
  scene: THREE.Scene;
  position?: THREE.Vector3;
  color?: number;
}

export class Player {
  public mesh: THREE.Group;
  private body: THREE.Mesh;
  private head: THREE.Mesh;

  // Movement
  private velocity: THREE.Vector3 = new THREE.Vector3();
  private moveSpeed: number = GAME_CONFIG.PLAYER_SPEED;
  private sprintMultiplier: number = GAME_CONFIG.PLAYER_SPRINT_MULTIPLIER;

  // PERF: Reusable vector for movement calculations
  private readonly _moveDir: THREE.Vector3 = new THREE.Vector3();

  // Click-to-move
  private moveTarget: THREE.Vector3 | null = null;
  private readonly _toTarget: THREE.Vector3 = new THREE.Vector3();

  // State
  private isGrounded: boolean = true;
  private isSprinting: boolean = false;

  // Animation
  private bobOffset: number = 0;
  private bobSpeed: number = 10;
  private bobAmount: number = 0.05;

  constructor(config: PlayerConfig) {
    this.mesh = new THREE.Group();

    // Create simple low-poly character
    // Body (capsule-like shape)
    const bodyGeometry = new THREE.CapsuleGeometry(0.4, 1, 4, 8);
    const bodyMaterial = new THREE.MeshStandardMaterial({
      color: config.color ?? 0x4a90d9,
      roughness: 0.7,
      metalness: 0.3,
    });
    this.body = new THREE.Mesh(bodyGeometry, bodyMaterial);
    this.body.position.y = 1;
    this.body.castShadow = true;
    this.body.receiveShadow = true;
    this.mesh.add(this.body);

    // Head (sphere)
    const headGeometry = new THREE.SphereGeometry(0.3, 8, 6);
    const headMaterial = new THREE.MeshStandardMaterial({
      color: 0xffdbac, // Skin tone
      roughness: 0.8,
      metalness: 0.1,
    });
    this.head = new THREE.Mesh(headGeometry, headMaterial);
    this.head.position.y = 1.9;
    this.head.castShadow = true;
    this.mesh.add(this.head);

    // Eyes
    const eyeGeometry = new THREE.SphereGeometry(0.05, 6, 4);
    const eyeMaterial = new THREE.MeshBasicMaterial({ color: 0x222222 });

    const leftEye = new THREE.Mesh(eyeGeometry, eyeMaterial);
    leftEye.position.set(-0.12, 1.95, 0.25);
    this.mesh.add(leftEye);

    const rightEye = new THREE.Mesh(eyeGeometry, eyeMaterial);
    rightEye.position.set(0.12, 1.95, 0.25);
    this.mesh.add(rightEye);

    // Set initial position
    if (config.position) {
      this.mesh.position.copy(config.position);
    }

    // Add to scene
    config.scene.add(this.mesh);
  }

  public update(deltaTime: number, input: InputState, camera: ThirdPersonCamera): void {
    this.handleMovement(deltaTime, input, camera);
    this.handleAnimation(deltaTime);
  }

  private handleMovement(deltaTime: number, input: InputState, camera: ThirdPersonCamera): void {
    // Get camera-relative directions
    const forward = camera.getForwardVector();
    const right = camera.getRightVector();

    // Calculate movement direction - reuse vector
    const moveDir = this._moveDir;
    moveDir.set(0, 0, 0);

    // Check for keyboard input first (cancels click-to-move)
    const hasKeyboardInput = input.forward || input.backward || input.left || input.right;

    if (hasKeyboardInput) {
      // Cancel click-to-move when using keyboard
      this.moveTarget = null;

      if (input.forward) {
        moveDir.add(forward);
      }
      if (input.backward) {
        moveDir.sub(forward);
      }
      if (input.right) {
        moveDir.add(right);
      }
      if (input.left) {
        moveDir.sub(right);
      }
    } else if (this.moveTarget) {
      // Click-to-move: move towards target
      this._toTarget.subVectors(this.moveTarget, this.mesh.position);
      this._toTarget.y = 0; // Keep on ground plane

      const distanceToTarget = this._toTarget.length();

      if (distanceToTarget < 0.5) {
        // Reached target
        this.moveTarget = null;
      } else {
        // Move towards target
        moveDir.copy(this._toTarget).normalize();
      }
    }

    // Normalize and apply speed
    if (moveDir.length() > 0) {
      moveDir.normalize();

      // Sprint check
      this.isSprinting = input.sprint;
      const speed = this.isSprinting
        ? this.moveSpeed * this.sprintMultiplier
        : this.moveSpeed;

      // Apply velocity
      this.velocity.x = moveDir.x * speed;
      this.velocity.z = moveDir.z * speed;

      // Rotate player to face movement direction
      const targetRotation = Math.atan2(moveDir.x, moveDir.z);
      this.mesh.rotation.y = THREE.MathUtils.lerp(
        this.mesh.rotation.y,
        targetRotation,
        0.15
      );
    } else {
      // Decelerate when not moving
      this.velocity.x *= 0.85;
      this.velocity.z *= 0.85;
    }

    // Apply gravity (simple)
    if (!this.isGrounded) {
      this.velocity.y -= 20 * deltaTime;
    }

    // Jump
    if (input.jump && this.isGrounded) {
      this.velocity.y = GAME_CONFIG.PLAYER_JUMP_FORCE;
      this.isGrounded = false;
    }

    // Apply velocity to position
    this.mesh.position.x += this.velocity.x * deltaTime;
    this.mesh.position.y += this.velocity.y * deltaTime;
    this.mesh.position.z += this.velocity.z * deltaTime;

    // Ground check (simple - assume y=0 is ground)
    if (this.mesh.position.y <= 0) {
      this.mesh.position.y = 0;
      this.velocity.y = 0;
      this.isGrounded = true;
    }
  }

  private handleAnimation(deltaTime: number): void {
    // Simple head bob when moving
    const isMoving = Math.abs(this.velocity.x) > 0.1 || Math.abs(this.velocity.z) > 0.1;

    if (isMoving) {
      const speed = this.isSprinting ? this.bobSpeed * 1.5 : this.bobSpeed;
      this.bobOffset += deltaTime * speed;
      const bob = Math.sin(this.bobOffset) * this.bobAmount;
      this.body.position.y = 1 + bob;
      this.head.position.y = 1.9 + bob;
    } else {
      // Reset bob
      this.bobOffset = 0;
      this.body.position.y = THREE.MathUtils.lerp(this.body.position.y, 1, 0.1);
      this.head.position.y = THREE.MathUtils.lerp(this.head.position.y, 1.9, 0.1);
    }
  }

  public getPosition(): THREE.Vector3 {
    return this.mesh.position.clone();
  }

  // PERF: Get position reference without cloning (read-only use)
  public getPositionRef(): THREE.Vector3 {
    return this.mesh.position;
  }

  public setPosition(x: number, y: number, z: number): void {
    this.mesh.position.set(x, y, z);
  }

  public getRotation(): number {
    return this.mesh.rotation.y;
  }

  public isPlayerSprinting(): boolean {
    return this.isSprinting;
  }

  // Click-to-move: set a target position to walk towards
  public setMoveTarget(target: THREE.Vector3): void {
    this.moveTarget = target.clone();
  }

  // Cancel click-to-move
  public clearMoveTarget(): void {
    this.moveTarget = null;
  }

  // Check if player is moving towards a click target
  public hasMoveTarget(): boolean {
    return this.moveTarget !== null;
  }

  public destroy(): void {
    this.mesh.parent?.remove(this.mesh);
    // Dispose geometries and materials
    this.body.geometry.dispose();
    (this.body.material as THREE.Material).dispose();
    this.head.geometry.dispose();
    (this.head.material as THREE.Material).dispose();
  }
}
